<!-- regular script shared between all component instances -->
<script lang="ts">
    import { inject, onMounted, provide, reactive, ref, watch } from 'vue'

    import { api } from '@cwd/lib/main'
    import { encodeJSONBlob, decodeJSONBlob, token } from '@cwd/lib/main'

    import vm, { tmp_uuidv4, UngappedJourney, resetActiveJourney } from './Journeys.viewmodel'

    // admin tools toggle
    import { toggleAdminTools } from './Journeys.entry'

    // components
    import Toast from '@journeys/components/Toast/Toast.vue'
    import JourneyStart from './Pages/JourneyStart.vue'
    import Viewport from './components/Viewport.vue'

    import InlineWorker from '@shared/modules/InlineWorker'
    import GraphData from './components/GraphData'
    import { deepClone, requestFrame } from '@shared/utils'
    import { JourneyStatus } from './Enums'
    import { listingsProcessor_reduce } from '@shared/utils/ListingsController'

    import { globalMethods } from '@shared/methods'
    // import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys'
    // import { FeatureService } from '@shared/directives/Feature.service'

    // const injectorAccess = inject(injectorAccessInjectionKey)!
    // const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService)

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'JourneyEditor',
    }
    export default {
        name: component.name,
    }
</script>

<!-- setup script is executed on each component instance -->
<script lang="ts" setup>
    import { setupTags } from '@shared/components/Tags/Tags'
    import { type TagsProvide } from '@shared/components/Tags/Tags.access'
    const tags = setupTags()

    provide<TagsProvide>('tags', {
        state: tags.state,
        create: tags.create,
        updateSelected: tags.updateSelected,
        toggle: tags.toggle,
    })

    function hasFeature(feature: any) {
        if (!viewModel?.hasFeature) return false
        return viewModel?.hasFeature(feature)
    }

    globalMethods.hasFeature = hasFeature

    //** redirect */
    if (!globalMethods.hasFeature('Automation.Journeys')) {
        window.location.href = '/Dashboard'
    }

    // admin tools
    if (globalMethods.hasFeature('AdminUtilities'))
        window.addEventListener('keydown', (ev: KeyboardEvent) => {
            if (ev.ctrlKey && ev.altKey && ev.code === 'KeyD') {
                ev.preventDefault()

                toggleAdminTools()
            }
        })

    const { editor, activeJourney, toast, account } = vm,
        state = reactive({
            init: false,
        })

    onMounted(async () => {
        awaitData()
    })

    const awaitData = async () => {
        state.init &&= false

        await new Promise((res: any) => (editor.init = res))

        editor.segmentGraph = new GraphData()
        state.init = true

        editor.history.cache.journey = deepClone(activeJourney)
    }

    const setupListings = () => {
        // TODO: syntax....
        account.listings.add('/tags', { map_key: 'TagId', eager: true })
        account.listings.add('/textmessages/listing', { map_key: 'SmsMessageId', eager: true })
        account.listings.add('/lists', { map_key: 'ListId' })
        account.listings.add('/segments/listing', { map_key: 'SegmentId' })
        account.listings.add('/issues/listing', { map_key: 'IssueId' })
        account.listings.add('/surveys/listing', { map_key: 'SurveyId', eager: true })
        account.listings.add('/events/listing', { map_key: 'EventId' })
    }

    account.listings.add('/tags', { map_key: 'TagId' })

    for (let endpoint of [`/journeys`, `/journeys/Templates`])
        account.listings.add(endpoint, {
            map_key: 'JourneyId',
            processors: [
                (data: any) => {
                    for (let item of data) {
                        item.data = JSON.parse(item.GraphicalData)
                    }
                },
            ],
            eager: true,
        })

    let routeWatcher = watch(
        () => vm.router.currentRoute.value,
        async () => {
            awaitData()

            await account.listings.get('/tags')

            if (vm.router.currentRoute.value.params.JourneyId) {
                api.get(`/journeys/${vm.router.currentRoute.value.params.JourneyId}${activeJourney.JourneyId ? '/EditorData' : ''}`).then((res: any) => {
                    if (res) {
                        let data = res.GraphicalData ? JSON.parse(res.GraphicalData) : activeJourney,
                            op_data = JSON.parse(res.OperationalData),
                            op_rootProps = ['JourneyId', 'steps', 'ownedObjects', 'startDateTime', 'scheduleController']

                        data.Status = res.Status

                        for (let key of op_rootProps) if (op_data[key]) data[key] = op_data[key]

                        for (let key in data.nodes) {
                            op_data?.nodes && op_data.nodes[key] && Object.assign(data.nodes[key].data, op_data.nodes[key])
                            data.nodes[key].runtime ||= {}
                        }

                        Object.assign(vm.activeJourney, data)
                    } else {
                        activeJourney.JourneyId = vm.router.currentRoute.value.params.JourneyId
                        activeJourney.Title = $Resources.CleanJourneyTitle
                        activeJourney.Status = JourneyStatus.Draft
                    }

                    state.init || vm.editor.init()

                    setupListings()
                })
            } else {
                if (activeJourney.JourneyId) resetActiveJourney()

                if (vm.router.currentRoute.value.path !== '/new')
                    await account.listings.get(`/journeys${vm.router.currentRoute.value.path}`, {
                        force_fetch: /[\w\-]{36}/.test(vm.router.options.history.state.forward),
                    })

                state.init ||= vm.editor.init()
            }
        }
    )
</script>

<template>
    <div class="app-container">
        <Suspense v-if="state.init">
            <router-view></router-view>
        </Suspense>

        <Toast />
    </div>
</template>

<style lang="less">
    /*
	.app-container:has(.workspace) {
		// temp fix
		display: block;
		position: relative;
		top: 0;
		left: 0;
		height: 100vh;
	}
    */

    ::-webkit-scrollbar {
        width: 0;
    }

    ::-webkit-scrollbar-track {
        background-color: transparent;
    }

    ::-webkit-scrollbar-thumb {
        background-color: transparent;
    }

    .editor-busy {
        pointer-events: none;
    }

    /** state classes */
    .prevent-select :where(.property-window, .modal-container) {
        user-select: none;
    }

    .prevent-select :where(.scrollbar-container) {
        pointer-events: none;
    }
</style>
